import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AnimatedSection from '../components/AnimatedSection';
import { API_BASE, fallbackPricing } from '../data/fallback';

/* ── Category icon map ── */
const categoryIcons = {
  'Website Development': (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/>
    </svg>
  ),
  'Search Engine Optimization': (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
  ),
  'Logo Design': (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
    </svg>
  ),
  'Google Business': (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
    </svg>
  ),
  'Ecommerce CMS': (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
    </svg>
  ),
};

const defaultIcon = (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8">
    <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
  </svg>
);

/* ── Why us features ── */
const whyUs = [
  { icon: <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>, title: 'No Hidden Fees', desc: 'Transparent pricing with everything included upfront.' },
  { icon: <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>, title: 'Fast Delivery', desc: 'Most projects delivered within the agreed timeline.' },
  { icon: <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>, title: 'Dedicated Support', desc: 'Post-launch support and maintenance included.' },
  { icon: <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>, title: '5-Star Quality', desc: 'Premium work that exceeds client expectations.' },
];

const Pricing = () => {
  const [plans, setPlans]     = useState(fallbackPricing);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE}/api/pricing`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => { if (data.success && data.data.length) setPlans(data.data); })
      .catch(() => setPlans(fallbackPricing))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <style>{`
        /* ── Grid ── */
        .pricing-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
          align-items: stretch;
        }

        /* ── Card base ── */
        .price-card {
          border-radius: 20px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          transition: transform 0.35s cubic-bezier(0.34,1.56,0.64,1),
                      box-shadow 0.35s ease;
          position: relative;
          cursor: default;
        }
        .price-card:hover { transform: translateY(-8px); z-index: 2; }

        /* Default card */
        .price-card-default {
          background: #ffffff;
          border: 1px solid #e9ecef;
          box-shadow: 0 2px 12px rgba(0,0,0,0.06);
        }
        .price-card-default:hover {
          box-shadow: 0 20px 48px rgba(22,22,90,0.13);
          border-color: transparent;
        }

        /* Featured card — dark premium */
        .price-card-featured {
          background: linear-gradient(160deg, #16165a 0%, #0d0d2b 100%);
          border: 1px solid rgba(255,255,255,0.08);
          box-shadow: 0 12px 40px rgba(22,22,90,0.45);
        }
        .price-card-featured:hover {
          box-shadow: 0 24px 60px rgba(22,22,90,0.55);
        }

        /* Top accent bar */
        .price-card-top-bar {
          height: 4px;
          width: 100%;
        }
        .price-card-default .price-card-top-bar {
          background: linear-gradient(90deg, #e8ecff, #e4e9ff);
        }
        .price-card-featured .price-card-top-bar {
          background: linear-gradient(90deg, #e8603a, #f08060);
        }

        /* Popular badge */
        .price-popular-badge {
          position: absolute;
          top: 20px; right: 20px;
          font-size: 0.65rem; font-weight: 800;
          padding: 4px 12px;
          border-radius: 50px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          display: flex; align-items: center; gap: 4px;
        }
        .price-card-default .price-popular-badge {
          background: #f0f2ff; color: #16165a;
          border: 1px solid #dde2ff;
        }
        .price-card-featured .price-popular-badge {
          background: #e8603a; color: white;
          box-shadow: 0 4px 12px rgba(232,96,58,0.4);
        }

        /* Card header */
        .price-card-header {
          padding: 28px 28px 24px;
          border-bottom: 1px solid rgba(0,0,0,0.06);
        }
        .price-card-featured .price-card-header {
          border-bottom-color: rgba(255,255,255,0.08);
        }

        /* Icon */
        .price-icon-wrap {
          width: 52px; height: 52px;
          border-radius: 14px;
          display: flex; align-items: center; justify-content: center;
          margin-bottom: 18px;
          transition: transform 0.3s ease;
        }
        .price-card:hover .price-icon-wrap { transform: scale(1.1) rotate(-4deg); }
        .price-card-default .price-icon-wrap {
          background: linear-gradient(135deg, #eef0ff, #e4e8ff);
          color: #16165a;
        }
        .price-card-featured .price-icon-wrap {
          background: rgba(255,255,255,0.1);
          color: #e8603a;
        }

        /* Category label */
        .price-category {
          font-size: 0.68rem; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.12em;
          margin-bottom: 6px;
        }
        .price-card-default  .price-category { color: #e8603a; }
        .price-card-featured .price-category { color: rgba(255,255,255,0.55); }

        /* Plan name */
        .price-name {
          font-size: 1.15rem; font-weight: 800;
          margin-bottom: 20px; line-height: 1.2;
          letter-spacing: -0.02em;
        }
        .price-card-default  .price-name { color: #111110; }
        .price-card-featured .price-name { color: white; }

        /* Price amount */
        .price-amount {
          display: flex; align-items: baseline; gap: 2px;
        }
        .price-rs {
          font-size: 1rem; font-weight: 700; margin-bottom: 2px;
        }
        .price-card-default  .price-rs { color: #6e6e66; }
        .price-card-featured .price-rs { color: rgba(255,255,255,0.65); }
        .price-num {
          font-size: 2.9rem; font-weight: 900; line-height: 1;
          letter-spacing: -0.04em;
        }
        .price-card-default  .price-num { color: #111110; }
        .price-card-featured .price-num { color: white; }
        .price-period {
          font-size: 0.78rem; margin-left: 6px;
        }
        .price-card-default  .price-period { color: #9ca3af; }
        .price-card-featured .price-period { color: rgba(255,255,255,0.45); }

        /* Features list */
        .price-features {
          padding: 22px 28px; flex: 1;
          list-style: none;
          display: flex; flex-direction: column; gap: 11px;
        }
        .price-feature-item {
          display: flex; align-items: center; gap: 10px;
          font-size: 0.875rem;
        }
        .price-card-default  .price-feature-item { color: #374151; }
        .price-card-featured .price-feature-item { color: rgba(255,255,255,0.85); }
        .price-feature-item.disabled {
          opacity: 0.35;
          text-decoration: line-through;
        }
        .pf-check {
          width: 20px; height: 20px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .price-card-default  .pf-check.yes { background: #f0fdf4; color: #16a34a; }
        .price-card-default  .pf-check.no  { background: #fef2f2; color: #dc2626; }
        .price-card-featured .pf-check.yes { background: rgba(34,197,94,0.18); color: #86efac; }
        .price-card-featured .pf-check.no  { background: rgba(255,255,255,0.08); color: rgba(255,255,255,0.35); }

        /* Divider inside card */
        .price-divider {
          margin: 0 28px;
          height: 1px;
          background: rgba(0,0,0,0.06);
        }
        .price-card-featured .price-divider {
          background: rgba(255,255,255,0.08);
        }

        /* Footer / CTA */
        .price-card-footer { padding: 22px 28px 28px; }
        .price-btn {
          display: flex; align-items: center; justify-content: center; gap: 8px;
          width: 100%; padding: 13px;
          border-radius: 10px; font-size: 0.9rem; font-weight: 700;
          text-align: center; text-decoration: none;
          cursor: pointer; border: none;
          transition: all 0.25s ease;
          letter-spacing: 0.01em;
          position: relative; overflow: hidden;
        }
        .price-btn::after {
          content: '';
          position: absolute; inset: 0;
          background: linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.16) 50%, transparent 60%);
          transform: translateX(-100%);
          transition: transform 0.5s ease;
        }
        .price-btn:hover::after { transform: translateX(100%); }
        .price-btn-default {
          background: transparent;
          border: 1.5px solid #e4e4e0;
          color: #16165a;
        }
        .price-btn-default:hover {
          background: #16165a; border-color: #16165a;
          color: white; transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(22,22,90,0.25);
        }
        .price-btn-featured {
          background: #e8603a; color: white;
          border: none;
          box-shadow: 0 4px 16px rgba(232,96,58,0.4);
        }
        .price-btn-featured:hover {
          background: #d4532e; transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(232,96,58,0.5);
        }

        /* ── Why Us strip ── */
        .why-us-strip {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 24px;
          margin-top: 64px;
          padding: 36px 40px;
          background: #f9f9f7;
          border-radius: 20px;
          border: 1px solid #e4e4e0;
        }
        .why-us-item {
          display: flex; align-items: flex-start; gap: 14px;
        }
        .why-us-icon {
          width: 42px; height: 42px;
          border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          background: linear-gradient(135deg, #eef0ff, #e4e8ff);
          color: #16165a;
          flex-shrink: 0;
          transition: transform 0.3s ease;
        }
        .why-us-item:hover .why-us-icon { transform: scale(1.1) rotate(-4deg); }
        .why-us-title { font-size: 0.9rem; font-weight: 700; color: #111110; margin-bottom: 4px; }
        .why-us-desc  { font-size: 0.8rem; color: #6e6e66; line-height: 1.6; }

        /* ── Pricing note ── */
        .pricing-note {
          text-align: center; margin-top: 32px;
          font-size: 0.84rem; color: #6e6e66;
          padding: 14px 20px;
          background: #f9f9f7;
          border-radius: 10px;
          border: 1px solid #e4e4e0;
          display: inline-flex;
          align-items: center; gap: 8px;
        }
        .pricing-note span { color: #e8603a; font-weight: 600; }

        /* ── CTA section ── */
        .pricing-cta {
          margin-top: 70px;
          background: linear-gradient(135deg, #1a1a4e 0%, #0e0e2e 50%, #131340 100%);
          border-radius: 20px;
          padding: 64px 48px;
          text-align: center;
          position: relative;
          overflow: hidden;
          border: 1px solid rgba(255,255,255,0.07);
        }
        /* subtle radial glow bottom-center */
        .pricing-cta::before {
          content: '';
          position: absolute;
          bottom: -60px; left: 50%;
          transform: translateX(-50%);
          width: 600px; height: 300px;
          background: radial-gradient(ellipse, rgba(79,100,247,0.18) 0%, transparent 70%);
          pointer-events: none;
        }
        .pricing-cta h2 {
          font-size: clamp(1.6rem, 3vw, 2.1rem);
          font-weight: 800; color: white;
          margin-bottom: 16px;
          position: relative; z-index: 1;
          letter-spacing: -0.03em;
        }
        .pricing-cta h2 span { color: #e8603a; }
        .pricing-cta p {
          color: rgba(255,255,255,0.58); font-size: 0.975rem;
          max-width: 520px; margin: 0 auto 36px;
          line-height: 1.85; position: relative; z-index: 1;
        }
        .pricing-cta-btns {
          display: flex; gap: 16px;
          justify-content: center; flex-wrap: wrap;
          position: relative; z-index: 1;
        }
        /* Orange filled pill */
        .cta-btn-primary {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 14px 32px;
          background: #e8603a;
          color: white;
          font-weight: 700; font-size: 0.925rem;
          border-radius: 9999px;
          border: none;
          text-decoration: none;
          cursor: pointer;
          transition: background 0.22s ease, transform 0.22s ease, box-shadow 0.22s ease;
          box-shadow: 0 4px 20px rgba(232,96,58,0.4);
          position: relative; overflow: hidden;
          letter-spacing: 0.01em;
        }
        .cta-btn-primary::after {
          content: '';
          position: absolute; inset: 0;
          background: linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.18) 50%, transparent 60%);
          transform: translateX(-100%);
          transition: transform 0.5s ease;
        }
        .cta-btn-primary:hover::after { transform: translateX(100%); }
        .cta-btn-primary:hover {
          background: #d4532e;
          transform: translateY(-2px);
          box-shadow: 0 8px 28px rgba(232,96,58,0.5);
        }
        /* White outline pill */
        .cta-btn-outline {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 13px 32px;
          background: transparent;
          color: white;
          font-weight: 700; font-size: 0.925rem;
          border-radius: 9999px;
          border: 1.5px solid rgba(255,255,255,0.45);
          text-decoration: none;
          cursor: pointer;
          transition: border-color 0.22s ease, background 0.22s ease, transform 0.22s ease;
          letter-spacing: 0.01em;
        }
        .cta-btn-outline:hover {
          border-color: rgba(255,255,255,0.85);
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
        }

        /* ── Loading ── */
        .pricing-loading {
          text-align: center; padding: 80px 20px;
          color: #6e6e66; font-size: 0.95rem;
          display: flex; align-items: center; justify-content: center; gap: 12px;
        }

        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }

        @media (max-width: 1024px) { .pricing-grid { grid-template-columns: 1fr 1fr; } .why-us-strip { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 640px)  { .pricing-grid { grid-template-columns: 1fr; } .pricing-cta { padding: 40px 24px; } .why-us-strip { grid-template-columns: 1fr; padding: 28px 24px; } }
      `}</style>

      {/* ── Hero ── */}
      <div className="page-hero">
        <div className="container">
          <AnimatedSection>
            <h1 className="page-hero-title">Our <span>Pricing</span></h1>
            <p className="page-hero-subtitle">Transparent, affordable pricing for every business need</p>
            <div className="breadcrumb">
              <Link to="/">Home</Link><span>›</span><span>Pricing</span>
            </div>
          </AnimatedSection>
        </div>
      </div>

      {/* ── Plans ── */}
      <section className="section-padding">
        <div className="container">
          <AnimatedSection className="section-header">
            <span className="section-tag">Affordable Plans</span>
            <h2 className="section-title">Choose Your <span>Package</span></h2>
            <div className="section-divider"><span></span><span></span><span></span></div>
            <p className="section-subtitle">
              Simple, honest pricing — no hidden fees. All plans include professional support from our expert team.
            </p>
          </AnimatedSection>

          {loading ? (
            <div className="pricing-loading">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#6e6e66" strokeWidth="2" style={{animation:'spin 0.8s linear infinite'}}><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/></svg>
              Loading pricing plans...
            </div>
          ) : (
            <div className="pricing-grid">
              {plans.map((plan, i) => {
                const isFeatured = !!plan.is_featured;
                const features   = Array.isArray(plan.features) ? plan.features : [];
                const icon       = categoryIcons[plan.category] || defaultIcon;
                return (
                  <AnimatedSection key={plan.id} animation="reveal" delay={i * 90}>
                    <div className={`price-card ${isFeatured ? 'price-card-featured' : 'price-card-default'}`}>

                      {/* Top color bar */}
                      <div className="price-card-top-bar" />

                      {/* Featured / tag badge */}
                      {plan.tag && (
                        <div className="price-popular-badge">
                          {isFeatured && (
                            <svg viewBox="0 0 24 24" width="10" height="10" fill="currentColor"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                          )}
                          {plan.tag}
                        </div>
                      )}

                      {/* Header */}
                      <div className="price-card-header">
                        <div className="price-icon-wrap">{icon}</div>
                        <div className="price-category">{plan.category}</div>
                        <div className="price-name">{plan.name}</div>
                        <div className="price-amount">
                          <span className="price-rs">₹</span>
                          <span className="price-num">{plan.price}</span>
                          <span className="price-period">/ project</span>
                        </div>
                      </div>

                      {/* Features */}
                      <div className="price-divider" />
                      <ul className="price-features">
                        {features.map((f, fi) => (
                          <li key={fi} className={`price-feature-item ${!f.ok ? 'disabled' : ''}`}>
                            <span className={`pf-check ${f.ok ? 'yes' : 'no'}`}>
                              {f.ok
                                ? <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                                : <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                              }
                            </span>
                            {f.text}
                          </li>
                        ))}
                      </ul>

                      {/* CTA */}
                      <div className="price-card-footer">
                        <Link to="/contact" className={`price-btn ${isFeatured ? 'price-btn-featured' : 'price-btn-default'}`}>
                          Get Started
                          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                        </Link>
                      </div>
                    </div>
                  </AnimatedSection>
                );
              })}
              {!plans.length && !loading && (
                <div style={{ gridColumn:'1/-1', textAlign:'center', color:'#9ca3af', padding:'60px' }}>No pricing plans available.</div>
              )}
            </div>
          )}

          {/* Pricing note */}
          <div style={{ textAlign:'center', marginTop:32 }}>
            <span className="pricing-note">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              All prices are exclusive of GST. &nbsp;<span>Custom packages available</span> — contact us for a tailored quote.
            </span>
          </div>

          {/* Why Us strip */}
          <AnimatedSection animation="reveal">
            <div className="why-us-strip">
              {whyUs.map((item, i) => (
                <div key={i} className="why-us-item">
                  <div className="why-us-icon">{item.icon}</div>
                  <div>
                    <div className="why-us-title">{item.title}</div>
                    <div className="why-us-desc">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </AnimatedSection>

          {/* Custom Plan CTA */}
          <AnimatedSection animation="reveal">
            <div className="pricing-cta">
              <h2>Need a <span>Custom Plan?</span></h2>
              <p>
                Every project is unique. Tell us your requirements and we'll create a custom package
                that fits your exact budget and goals.
              </p>
              <div className="pricing-cta-btns">
                <Link to="/contact#quote" className="cta-btn-primary">
                  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" style={{flexShrink:0}}><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                  Request Custom Quote
                </Link>
                <Link to="/services" className="cta-btn-outline">
                  View All Services
                </Link>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
